import type { ActivityType, Days } from "api/services/treatment/schemas/ScheduleWeek";

import type { ProtocolCard } from "../PatientProfile/components/Protocol/ProtocolTimeline/types";

export const handleType = (type: string): ActivityType => {
  if (type === "functionality_1") return "functionality" as ActivityType;

  switch (type) {
    case "weekly_health_report":
    case "w1_health_report":
    case "followup_health_report":
    case "goal":
      return "report";
    case "text_lesson":
      return "lesson";
    case "exercise":
    case "functionality":
      return type;
    case "subscription":
    case "update_payment_method":
      return "payment";
    default:
      return "activity";
  }
};

export const generateLibraryPath = (activityType: string) => {
  const type = activityType.split("_")[0];
  const id = activityType.split("_")[1];
  return `/library/your-library/${type}s/${id}`;
};

export const getTextLessonPart = (activityName: string): string => {
  return activityName.substring(activityName.lastIndexOf("_") + 1);
};

export const isTranslatedActivity = (type: string): boolean => {
  return type === "exercise" || type === "functionality" || type === "text_lesson";
};

export const sortActivities = (a: ProtocolCard, b: ProtocolCard): number => {
  const TYPES = ["payment", "activity", "report", "functionality", "lesson", "exercise"];
  const x = TYPES.indexOf(a.activity_type_trimmed);
  const y = TYPES.indexOf(b.activity_type_trimmed);
  if (x === y) {
    return a.completed_on === null ? 1 : -1;
  }
  return x < y ? -1 : 1;
};

export function getOverdueActivities(days: Days) {
  if (!days || Object.keys(days).length === 0) return [];

  const overdueActivities: ProtocolCard[] = [];

  Object.values(days).forEach(day => {
    const allActivities = [...day.activities, ...day.exercises, ...day.lessons].map(activity => ({
      ...activity,
      activity_type_trimmed: handleType(activity.activity_type),
      activityID: activity.id,
    }));
    const overdueInDay = allActivities.filter(activity => activity.overdue);
    overdueActivities.push(...(overdueInDay as ProtocolCard[]));
  });

  return overdueActivities;
}

export function hasOverdueActivities(days: Days): boolean {
  if (!days || Object.keys(days).length === 0) return false;

  return Object.values(days).some(day =>
    [...day.activities, ...day.exercises, ...day.lessons]?.some(activity => activity.overdue)
  );
}

export function hasRequiredActivities(days: Days, dayIndex?: number): boolean {
  if (dayIndex) {
    const day = days[dayIndex];
    return day ? day.activities.some(activity => activity.required) : false;
  }

  return Object.values(days).some(day => day.activities.some(activity => activity.required));
}

export function hasOverdueRequiredActivities(days: Days): boolean {
  if (!days || Object.keys(days).length === 0) return false;

  return Object.values(days).some(day => day.activities.some(activity => activity.overdue && activity.required));
}
